/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import prettyBytes from 'pretty-bytes';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ServiceType } from 'shared';

import { UnitsToBytesMultiplier } from '../../elements/HUBytesInput/components/UnitPicker.types';
import { Namespaces } from '../../i18n';
import { useSystemConfig } from '../../providers/SystemConfigProvider';
import { estimatePrice } from '../../utils/getEstimation';
import { PriceItem } from './PriceItem/PriceItem';
import { SpecsGrid } from './SpecsGrid/SpecsGrid';

interface Props {
  cpuUnits: number;
  memoryGbs: number;
  storageGbs: number;
  replicas: number;
  supportPlan: { name: string; description: string; price: number };
  postgresVersion?: number;
  serviceType: ServiceType | undefined;
}

export const PriceEstimation = memo<Props>(props => {
  const { t } = useTranslation();

  const systemConfig = useSystemConfig();

  const { daily, monthly } = useMemo(
    () =>
      estimatePrice({
        cpuPricePerMonth: systemConfig.cpuPricePerMonth,
        memoryPricePerMonth: systemConfig.memoryPricePerMonth,
        storagePricePerMonth: systemConfig.storagePricePerMonth,
        supportPricePerMonth: Number(props.supportPlan.price),
        cpuUnits: props.cpuUnits,
        memoryGbs: props.memoryGbs,
        storageGbs: props.storageGbs,
        replicas: props.replicas,
      }),
    [
      props.cpuUnits,
      props.memoryGbs,
      props.replicas,
      props.storageGbs,
      props.supportPlan.price,
      systemConfig.cpuPricePerMonth,
      systemConfig.memoryPricePerMonth,
      systemConfig.storagePricePerMonth,
    ],
  );

  const isPostgres = useMemo(
    () => props.serviceType !== 'cypex' && props.serviceType !== 'migrator',
    [props.serviceType],
  );

  const isShowTotal = useMemo(
    () => systemConfig.showInstanceCost || systemConfig.showSupportCost,
    [systemConfig.showInstanceCost, systemConfig.showSupportCost],
  );

  return (
    <>
      <PriceItem
        title={t('cluster:price_estimation.instance')}
        showPrice={systemConfig.showInstanceCost}
        price={{ monthly: monthly.instance, daily: daily.instance, currency: systemConfig.currency }}
      />
      <SpecsGrid
        specs={[
          {
            name: t('cluster:price_estimation.cores'),
            value: t('cluster:price_estimation.specs.cores', { count: props.cpuUnits }),
          },
          {
            name: t('cluster:price_estimation.memory'),
            value: prettyBytes(props.memoryGbs * UnitsToBytesMultiplier['GB']),
          },
          {
            name: t('cluster:price_estimation.storage'),
            value: prettyBytes(props.storageGbs * UnitsToBytesMultiplier['GB']),
          },
        ]}
      />

      <PriceItem
        title={t('cluster:price_estimation.support_plan.title')}
        showPrice={systemConfig.showSupportCost}
        price={{ monthly: monthly.support, daily: daily.support, currency: systemConfig.currency }}
      />
      <SpecsGrid
        specs={[
          { name: t('cluster:price_estimation.support_plan.name'), value: props.supportPlan.name },
          { name: t('cluster:price_estimation.support_plan.description'), value: props.supportPlan.description },
        ]}
      />

      {props.postgresVersion && isPostgres && (
        <PriceItem title={t(`${Namespaces.Admin}:postgres_version.label`)} unit={props.postgresVersion} />
      )}

      {isPostgres && <PriceItem title={t('cluster:price_estimation.replicas')} unit={props.replicas} />}

      {isShowTotal && (
        <PriceItem
          isTotal
          title={t('cluster:price_estimation.total')}
          price={{ monthly: monthly.total, daily: daily.total, currency: systemConfig.currency }}
        />
      )}
    </>
  );
});
